import { Component, EventEmitter, OnInit } from '@angular/core';
import { BankingAccountTypeService } from '../banking-account-type.service';
import { BankingAccountType } from '../banking-account-type';
import { NotificationService } from '../../../../shared/notification.service';

@Component({
  selector: 'app-banking-account-type-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class BankingAccountTypeListComponent implements OnInit {
  public accountType: BankingAccountType;

  /**
   * A list of all the banking-account-types currently loaded.
   *
   * @type {BankingAccountType[]}
   */
  public accountTypes: BankingAccountType[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of banking-account-types.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of banking-account-types that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of banking-account-types that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a accountType has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public accountTypeDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {BankingAccountTypeService} accountTypeService
   * @param {NotificationService} notificationService
   */
  constructor(
    private accountTypeService: BankingAccountTypeService,
    private notificationService: NotificationService,
  ) {
    this.accountTypeService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.accountTypeService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.accountTypeService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.accountTypeService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the accountType list.
   */
  public ngOnInit(): void {
    this.loadBankingAccountTypes(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * accountType list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadBankingAccountTypes(pageNumber);
  }

  /**
   * Loads a list of banking-account-types using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadBankingAccountTypes(page?: number, limit?: number): void {
    const listBankingAccountTypes = this.accountTypeService
      .listBankingAccountTypes(page, limit)
      .subscribe((accountTypes: BankingAccountType[]) => {
        this.accountTypes = accountTypes;
        listBankingAccountTypes.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate accountType ID
   * to delete the accountType.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.accountTypeService.deleteBankingAccountType(id).subscribe(
      deleted => {
        this.notificationService.success(
          `BankingAccountType Deleted`,
          `BankingAccountType with ID ${id} was deleted.`,
          5,
        );
        this.accountTypeDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `BankingAccountType Not Deleted`,
          err.error.message,
          10,
        );
        this.accountTypeDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
